import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  ArrowBackIosNew as ArrowBackIosNewIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
} from '@mui/icons-material'
import { getStorage, ref, getDownloadURL } from '@firebase/storage'
import { ImageMagnifier } from '@/components/ImageMagnifier'

export interface MediaGalleryItem {
  key: string
  storagePath: string
  /** When set (e.g. API signed URL), used instead of loading from Firebase Storage. */
  prefetchedUrl?: string
  mediaKind: 'photo' | 'video'
  noteText: string
  metaText: string
}

function useWorkNoteMediaDownloadUrl(storagePath: string | undefined, prefetchedUrl?: string) {
  const [url, setUrl] = useState<string | null>(prefetchedUrl ?? null)
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    prefetchedUrl ? 'ready' : 'idle'
  )

  useEffect(() => {
    if (prefetchedUrl) {
      setUrl(prefetchedUrl)
      setStatus('ready')
      return
    }
    if (!storagePath) {
      setUrl(null)
      setStatus('idle')
      return
    }
    let cancelled = false
    setStatus('loading')
    const storage = getStorage()
    getDownloadURL(ref(storage, storagePath))
      .then((u) => {
        if (!cancelled) {
          setUrl(u)
          setStatus('ready')
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUrl(null)
          setStatus('error')
        }
      })
    return () => {
      cancelled = true
    }
  }, [storagePath, prefetchedUrl])

  return { url, status }
}

function downloadPhotoFromUrl(url: string, filename: string) {
  return fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
      return response.blob()
    })
    .then((blob) => {
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    })
}

function photoDownloadFilename(storagePath: string) {
  const base = storagePath.split('/').pop() || 'repair-photo'
  const withoutQuery = base.split('?')[0]
  if (/\.[a-z0-9]+$/i.test(withoutQuery)) return withoutQuery
  return `${withoutQuery}.jpg`
}

function MediaCarousel({
  title,
  items,
}: {
  title: string
  items: MediaGalleryItem[]
}) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [photoRotations, setPhotoRotations] = useState<Record<string, number>>({})
  const [isDownloading, setIsDownloading] = useState(false)
  const currentItem = items[activeIndex]
  const { url, status } = useWorkNoteMediaDownloadUrl(currentItem?.storagePath, currentItem?.prefetchedUrl)
  const currentRotation = currentItem ? (photoRotations[currentItem.key] ?? 0) : 0

  useEffect(() => {
    setActiveIndex(0)
  }, [items])

  if (items.length === 0) {
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {title} (0)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No {title.toLowerCase()} in this group.
        </Typography>
      </Box>
    )
  }

  const movePrevious = () => {
    setActiveIndex((prev) => (prev - 1 + items.length) % items.length)
  }
  const moveNext = () => {
    setActiveIndex((prev) => (prev + 1) % items.length)
  }

  const rotatePhoto = (delta: -90 | 90) => {
    if (!currentItem || currentItem.mediaKind !== 'photo') return
    setPhotoRotations((prev) => {
      const current = prev[currentItem.key] ?? 0
      const next = ((current + delta) % 360 + 360) % 360
      return { ...prev, [currentItem.key]: next }
    })
  }

  const handleDownloadPhoto = async () => {
    if (!url || !currentItem || currentItem.mediaKind !== 'photo' || isDownloading) return
    setIsDownloading(true)
    try {
      await downloadPhotoFromUrl(url, photoDownloadFilename(currentItem.storagePath))
    } catch (error) {
      console.error('Error downloading photo:', error)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">
          {title} ({items.length})
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {activeIndex + 1} / {items.length}
        </Typography>
      </Box>
      <Paper variant="outlined" sx={{ p: 1.25 }}>
        <Box
          sx={{
            borderRadius: 1,
            overflow: 'visible',
            bgcolor: 'grey.900',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            maxHeight: 420,
            minHeight: 220,
            mb: 1.25,
          }}
        >
          {status === 'loading' ? (
            <Typography variant="body2" color="grey.100">
              Loading media...
            </Typography>
          ) : status === 'error' || !url ? (
            <Typography variant="body2" color="error.light">
              Could not load media
            </Typography>
          ) : currentItem.mediaKind === 'photo' ? (
            <ImageMagnifier
              key={currentItem.key}
              src={url}
              alt={currentItem.noteText || 'Photo'}
              rotation={currentRotation}
              sx={{ width: '100%', height: '100%', maxHeight: 420 }}
              imgSx={{ maxHeight: 420 }}
            />
          ) : (
            <Box
              component="video"
              src={url}
              controls
              playsInline
              preload="metadata"
              sx={{
                maxWidth: '100%',
                maxHeight: 420,
                display: 'block',
              }}
            />
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            mb: 1,
          }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<ArrowBackIosNewIcon fontSize="small" />}
            onClick={movePrevious}
            disabled={items.length <= 1}
            sx={{ textTransform: 'none' }}
          >
            Previous
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            {currentItem.mediaKind === 'photo' ? (
              <>
                <Tooltip title="Rotate left (90°)">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => rotatePhoto(-90)}
                      disabled={!url}
                      aria-label="Rotate left"
                    >
                      <RotateLeftIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Rotate right (90°)">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => rotatePhoto(90)}
                      disabled={!url}
                      aria-label="Rotate right"
                    >
                      <RotateRightIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon fontSize="small" />}
                  onClick={() => void handleDownloadPhoto()}
                  disabled={!url || isDownloading}
                  sx={{ textTransform: 'none' }}
                >
                  Download
                </Button>
              </>
            ) : null}
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon fontSize="small" />}
              onClick={() => {
                if (!url) return
                window.open(url, '_blank', 'noopener,noreferrer')
              }}
              disabled={!url}
              sx={{ textTransform: 'none' }}
            >
              Open
            </Button>
          </Box>
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowForwardIosIcon fontSize="small" />}
            onClick={moveNext}
            disabled={items.length <= 1}
            sx={{ textTransform: 'none' }}
          >
            Next
          </Button>
        </Box>
        {currentItem.noteText ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {currentItem.noteText}
          </Typography>
        ) : null}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {currentItem.metaText}
        </Typography>
      </Paper>
    </Box>
  )
}

const MediaGalleryDialog = ({
  open,
  onClose,
  photos,
  videos,
}: {
  open: boolean
  onClose: () => void
  photos: MediaGalleryItem[]
  videos: MediaGalleryItem[]
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Media</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {photos.length > 0 ? <MediaCarousel title="Photos" items={photos} /> : null}
          {videos.length > 0 ? <MediaCarousel title="Videos" items={videos} /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default MediaGalleryDialog
